/* eslint-disable react-hooks/rules-of-hooks */
import ActionCarCulatorLink from '@components/ActionCarCulatorLink';
import { getRelativeIntValue } from '@components/cars-table-next/columns/lib';
import CarsTableValuationCountryIndicator from '@components/CarsTableValuationCountryIndicator';
import ReferenceVehicleButton from '@components/ReferenceVehicleButton';
import { Stack } from '@components/ui';
import { Grid } from '@material-ui/core';
import { has } from 'lodash';
import { useUser } from 'src/hooks/useUser';
import { ConditionalFormattingType, CountryCode, ValuationCountryCode } from 'src/modules/generated/api';
import { Potential } from 'src/types/potential';

import type { Get } from 'type-fest';

import useRole from '../../../hooks/useRole';
import UserRole from '../../../types/UserRoles';
import { ContentCell, NumericCell, NumericCellFallback, TextCellFallback } from '../cells';
import { ColumnSize } from '../constants';
import { BidsTableNextItem, CarsTableNextItem, StrictColumnDef } from '../types';

import { createCountryPotentialColumn, createPotentialColumn } from './common/templates';

export const externalColumns = (enableSorting: boolean = true): StrictColumnDef<CarsTableNextItem>[] => [
  {
    id: 'referenceVehicles',
    cell: ({ row: { original } }) => {
      const { data: user } = useUser();

      // for packages we only show the disabled button, no special handling necessary

      return (
        <ContentCell>
          <Stack direction="row" spacing={1} justifyContent="center">
            {user?.dealer?.country === CountryCode.At && (
              <ActionCarCulatorLink
                firstRegistration={original.firstRegistration}
                mileage={original.mileage}
                vin={original.vin}
                layout="vertical"
              />
            )}
            {original.valuation?.numberOfCars && original.valuation.numberOfCars > 0 && (
              <ReferenceVehicleButton
                refCar={original}
                potential={original.potential}
                offerId={original.valuation.offerId}
                valuationCountry={original.valuation.valuationCountry}
              />
            )}
          </Stack>
        </ContentCell>
      );
    },
    meta: {
      align: 'center',
      export: {
        enabled: false,
      },
      fallback: () => <TextCellFallback textWidth="80%" style={{ textAlign: 'center' }} />,
      isExternalColumn: true,
      isCustomerModeHidden: true,
    },
    enableSorting: false,
  },
  {
    id: 'marketPrice',
    accessorFn: (car) => car.valuation?.price?.mean,
    cell: ({ getValue, row: { original } }) => {
      const { hasRole } = useRole();

      // Package bid: use packageAggregation.valuation.price.mean
      if ((original as BidsTableNextItem).isPackageBid) {
        const avgPrice = original.packageAggregation?.valuation?.price?.mean;
        const valuationCountry = original.packageAggregation?.valuation?.valuationCountryCode;
        return (
          <ContentCell>
            {(hasRole(UserRole.ADMIN) || hasRole(UserRole.COUNTRYMANAGER)) && valuationCountry && (
              <CarsTableValuationCountryIndicator valuationCountry={valuationCountry} />
            )}
            <NumericCell value={avgPrice ?? '-'} />
          </ContentCell>
        );
      }

      const valuationCountry = original?.potential?.country;
      return (
        <ContentCell>
          {(hasRole(UserRole.ADMIN) || hasRole(UserRole.COUNTRYMANAGER)) && valuationCountry && (
            <CarsTableValuationCountryIndicator valuationCountry={valuationCountry} />
          )}
          <NumericCell value={getValue<Get<CarsTableNextItem, 'valuation.price.mean'>>() ?? '-'} />
        </ContentCell>
      );
    },
    meta: {
      isNumeric: true,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      isExternalColumn: true,
      isCustomerModeHidden: true,
      conditionalFormat: ConditionalFormattingType.ValuationMarketPrice,
    },
    enableSorting,
  },
  {
    id: 'marketPriceDE',
    accessorFn: (car) => car.valuations?.DE?.price?.mean,
    cell: ({ getValue }) => {
      const valuationCountry = ValuationCountryCode.De;
      return (
        <ContentCell>
          {valuationCountry && <CarsTableValuationCountryIndicator valuationCountry={valuationCountry} />}
          <NumericCell value={getValue<Get<CarsTableNextItem, 'valuations.DE.price.mean'>>() ?? '-'} />
        </ContentCell>
      );
    },
    meta: {
      isNumeric: true,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      isExternalColumn: true,
      isCustomerModeHidden: true,
      export: {
        enabled: true,
      },
    },
    enableSorting: false,
  },
  {
    id: 'referenceVehiclesDE',
    cell: ({ row: { original } }) => (
      <ContentCell>
        <CarsTableValuationCountryIndicator valuationCountry={ValuationCountryCode.De} />
        <Grid container justifyContent="center">
          {has(original.valuations?.DE, 'numberOfCars') && original.valuations?.DE.numberOfCars! > 0 && (
            <Grid item>
              <ReferenceVehicleButton
                refCar={original}
                potential={original.potentials?.DE}
                offerId={original.valuations?.DE.offerId}
                valuationCountry={original.valuations?.DE.valuationCountry}
              />
            </Grid>
          )}
        </Grid>
      </ContentCell>
    ),
    meta: {
      align: 'center',
      export: {
        enabled: false,
      },
      fallback: () => <TextCellFallback textWidth="80%" style={{ textAlign: 'center' }} />,
      isExternalColumn: true,
      isCustomerModeHidden: true,
    },
    size: ColumnSize.xs,
    enableSorting: false,
  },
  // CZ market price (shown to SK users only — filtered in CarsTableNext/BidsTableNext)
  {
    id: 'marketPriceCZ',
    accessorFn: (car) => car.valuations?.CZ?.price?.mean,
    cell: ({ getValue }) => {
      const valuationCountry = ValuationCountryCode.Cz;
      return (
        <ContentCell>
          {valuationCountry && <CarsTableValuationCountryIndicator valuationCountry={valuationCountry} />}
          <NumericCell value={getValue<Get<CarsTableNextItem, 'valuations.CZ.price.mean'>>() ?? '-'} />
        </ContentCell>
      );
    },
    meta: {
      isNumeric: true,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      isExternalColumn: true,
      isCustomerModeHidden: true,
      export: {
        enabled: true,
      },
    },
    enableSorting: false,
  },
  // SK market price (shown to CZ users only — filtered in CarsTableNext/BidsTableNext)
  {
    id: 'marketPriceSK',
    accessorFn: (car) => car.valuations?.SK?.price?.mean,
    cell: ({ getValue }) => {
      const valuationCountry = ValuationCountryCode.Sk;
      return (
        <ContentCell>
          {valuationCountry && <CarsTableValuationCountryIndicator valuationCountry={valuationCountry} />}
          <NumericCell value={getValue<Get<CarsTableNextItem, 'valuations.SK.price.mean'>>() ?? '-'} />
        </ContentCell>
      );
    },
    meta: {
      isNumeric: true,
      fallback: () => <NumericCellFallback textWidth="80%" />,
      isExternalColumn: true,
      isCustomerModeHidden: true,
      export: {
        enabled: true,
      },
    },
    enableSorting: false,
  },
  {
    id: 'referenceVehiclesCZ',
    cell: ({ row: { original } }) => (
      <ContentCell>
        <CarsTableValuationCountryIndicator valuationCountry={ValuationCountryCode.Cz} />
        <Grid container justifyContent="center">
          {has(original.valuations?.CZ, 'numberOfCars') && original.valuations?.CZ.numberOfCars! > 0 && (
            <Grid item>
              <ReferenceVehicleButton
                refCar={original}
                potential={original.potentials?.CZ}
                offerId={original.valuations?.CZ.offerId}
                valuationCountry={original.valuations?.CZ.valuationCountry}
              />
            </Grid>
          )}
        </Grid>
      </ContentCell>
    ),
    meta: {
      align: 'center',
      export: {
        enabled: false,
      },
      fallback: () => <TextCellFallback textWidth="80%" style={{ textAlign: 'center' }} />,
      isExternalColumn: true,
      isCustomerModeHidden: true,
    },
    size: ColumnSize.xs,
    enableSorting: false,
  },
  {
    id: 'referenceVehiclesSK',
    cell: ({ row: { original } }) => (
      <ContentCell>
        <CarsTableValuationCountryIndicator valuationCountry={ValuationCountryCode.Sk} />
        <Grid container justifyContent="center">
          {has(original.valuations?.SK, 'numberOfCars') && original.valuations?.SK.numberOfCars! > 0 && (
            <Grid item>
              <ReferenceVehicleButton
                refCar={original}
                potential={original.potentials?.SK}
                offerId={original.valuations?.SK.offerId}
                valuationCountry={original.valuations?.SK.valuationCountry}
              />
            </Grid>
          )}
        </Grid>
      </ContentCell>
    ),
    meta: {
      align: 'center',
      export: {
        enabled: false,
      },
      fallback: () => <TextCellFallback textWidth="80%" style={{ textAlign: 'center' }} />,
      isExternalColumn: true,
      isCustomerModeHidden: true,
    },
    size: ColumnSize.xs,
    enableSorting: false,
  },
  createPotentialColumn(
    Potential.potentialMeanAbsolute,
    {
      isExternalColumn: true,
      isCustomerModeHidden: true,
      conditionalFormat: ConditionalFormattingType.PotentialInternetPriceAverageAbsolute,
    },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Cz,
    Potential.potentialMeanAbsolute,
    { isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Sk,
    Potential.potentialMeanAbsolute,
    { isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createPotentialColumn(
    Potential.potentialMeanRelative,
    {
      isExternalColumn: true,
      isCustomerModeHidden: true,
      conditionalFormat: {
        key: ConditionalFormattingType.PotentialInternetPriceAveragePercent,
        getValue: ({ getValue }) =>
          getRelativeIntValue(getValue<Get<CarsTableNextItem, 'potential.potentialMeanRelative'>>()),
      },
    },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Cz,
    Potential.potentialMeanRelative,
    { isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Sk,
    Potential.potentialMeanRelative,
    { isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createPotentialColumn(
    Potential.potentialMinAbsolute,
    {
      isExternalColumn: true,
      isCustomerModeHidden: true,
      conditionalFormat: ConditionalFormattingType.PotentialInternetPriceMinAbsolute,
    },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Cz,
    Potential.potentialMinAbsolute,
    { isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Sk,
    Potential.potentialMinAbsolute,
    { isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createPotentialColumn(
    Potential.potentialMinRelative,
    {
      isExternalColumn: true,
      isCustomerModeHidden: true,
      conditionalFormat: {
        key: ConditionalFormattingType.PotentialInternetPriceMinPercent,
        getValue: ({ getValue }) =>
          getRelativeIntValue(getValue<Get<CarsTableNextItem, 'potential.potentialMinRelative'>>()),
      },
    },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Cz,
    Potential.potentialMinRelative,
    { isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Sk,
    Potential.potentialMinRelative,
    { isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createPotentialColumn(
    Potential.potentialMinMeanAbsolute,
    {
      columnVisible: false,
      isExternalColumn: true,
      isCustomerModeHidden: true,
      conditionalFormat: ConditionalFormattingType.PotentialInternetPriceMinAverageAbsolute,
    },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Cz,
    Potential.potentialMinMeanAbsolute,
    { columnVisible: false, isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Sk,
    Potential.potentialMinMeanAbsolute,
    { columnVisible: false, isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createPotentialColumn(
    Potential.potentialMinMeanRelative,
    {
      columnVisible: false,
      isExternalColumn: true,
      isCustomerModeHidden: true,
      conditionalFormat: {
        key: ConditionalFormattingType.PotentialInternetPriceMinAveragePercent,
        getValue: ({ getValue }) =>
          getRelativeIntValue(getValue<Get<CarsTableNextItem, 'potential.potentialMinMeanRelative'>>()),
      },
    },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Cz,
    Potential.potentialMinMeanRelative,
    { columnVisible: false, isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
  createCountryPotentialColumn(
    ValuationCountryCode.Sk,
    Potential.potentialMinMeanRelative,
    { columnVisible: false, isExternalColumn: true, isCustomerModeHidden: true },
    enableSorting,
  ),
];
